import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { MoodRing } from "@/components/MoodRing";
import { MirrorCard } from "@/components/MirrorCard";
import { IntensitySlider, TimeOfDaySelector, ContextTagPicker } from "@/components/CaptureControls";
import { apiRequest, refreshLogDerivedData } from "@/lib/queryClient";
import { SECONDARY_MOODS, type Mood } from "@shared/schema";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";
import { ChevronDown, ChevronUp } from "lucide-react";

const MAX_CHARS = 280;

function EnergyMeter({ value, onChange, accentColor }: {
  value: number | null;
  onChange: (v: number) => void;
  accentColor: string;
}) {
  return (
    <div className="flex gap-1.5 items-center">
      <div className="relative flex h-11 items-center">
        <div className="flex h-7 items-end gap-1.5" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => {
            const level = i + 1;
            const isActive = value !== null && level <= value;
            const barHeight = 8 + i * 2;
            return (
              <div
                key={i}
                className="w-3 transition-all duration-200 rounded-sm"
                style={{
                  height: `${barHeight}px`,
                  backgroundColor: isActive ? accentColor : "var(--surface-2)",
                  border: `1px solid ${isActive ? `color-mix(in srgb, ${accentColor} 38%, transparent)` : "var(--border-hairline)"}`,
                }}
                data-testid={`energy-bar-${level}`}
              />
            );
          })}
        </div>
        <input
          type="range"
          min={0}
          max={10}
          value={value ?? 0}
          onChange={(event) => {
            const nextValue = Number(event.target.value);
            if (nextValue > 0) onChange(nextValue);
          }}
          aria-label="Energy level"
          aria-valuetext={value === null ? "Not set" : `${value} of 10`}
          className="absolute inset-0 h-11 w-full cursor-pointer opacity-0"
          data-testid="input-energy-level"
        />
      </div>
      <span
        className="text-xs ml-2"
        style={{ color: value ? accentColor : "var(--text-muted)", transition: "color 0.3s", fontFamily: "var(--font-display)" }}
      >
        {value ? `${value}/10` : "—"}
      </span>
    </div>
  );
}

function SleepMeter({ value, onChange }: {
  value: number | null;
  onChange: (v: number) => void;
}) {
  const sleepLabels = ["", "wrecked", "rough", "broken", "light", "okay", "decent", "solid", "deep", "perfect", "transcendent"];

  return (
    <div className="flex gap-1 items-center">
      <div className="relative flex h-11 items-center">
        <div className="flex gap-1" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => {
            const level = i + 1;
            const isActive = value !== null && level <= value;
            const moonOpacity = isActive ? 0.7 + (i * 0.03) : 0.06;
            return (
              <div
                key={i}
                className="w-3 h-3 transition-all duration-200 rounded-sm"
                style={{
                  backgroundColor: isActive ? `color-mix(in srgb, var(--accent-color) ${moonOpacity * 100}%, transparent)` : "var(--surface-2)",
                  border: `1px solid ${isActive ? "var(--accent-border-strong)" : "var(--border-hairline)"}`,
                }}
                data-testid={`sleep-bar-${level}`}
              />
            );
          })}
        </div>
        <input
          type="range"
          min={0}
          max={10}
          value={value ?? 0}
          onChange={(event) => {
            const nextValue = Number(event.target.value);
            if (nextValue > 0) onChange(nextValue);
          }}
          aria-label="Sleep quality"
          aria-valuetext={value === null ? "Not set" : `${sleepLabels[value]}, ${value} of 10`}
          className="absolute inset-0 h-11 w-full cursor-pointer opacity-0"
          data-testid="input-sleep-quality"
        />
      </div>
      <span
        className="text-xs font-mono ml-2"
        style={{ color: value ? "var(--accent-color)" : "var(--text-muted)" }}
      >
        {value ? sleepLabels[value] : "—"}
      </span>
    </div>
  );
}

export default function Capture() {
  const [mood, setMood] = useState<Mood>("calm");
  const [moodIntensity, setMoodIntensity] = useState(0.5);
  const [secondaryMood, setSecondaryMood] = useState<string | null>(null);
  const [energyLevel, setEnergyLevel] = useState<number | null>(null);
  const [sleepQuality, setSleepQuality] = useState<number | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [timeOfDay, setTimeOfDay] = useState<string | null>(null);
  const [moment, setMoment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const queryClient = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ["/api/status"],
  });

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) setTimeOfDay("morning");
    else if (hour >= 12 && hour < 17) setTimeOfDay("afternoon");
    else if (hour >= 17 && hour < 22) setTimeOfDay("evening");
    else setTimeOfDay("late night");
  }, []);

  const handleMoodChange = (newMood: Mood) => {
    setMood(newMood);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const logMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/logs", data),
    onSuccess: () => {
      refreshLogDerivedData();
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setMoment("");
        setSelectedTags([]);
        setSecondaryMood(null);
        setEnergyLevel(null);
        setSleepQuality(null);
        setMoodIntensity(0.5);
      }, 2800);
    },
  });

  const handleSubmit = () => {
    logMutation.mutate({
      mood,
      moodIntensity,
      secondaryMood: secondaryMood || undefined,
      energyLevel: energyLevel || undefined,
      sleepQuality: sleepQuality || undefined,
      tags: selectedTags,
      moment,
      timeOfDay: timeOfDay || undefined,
    });
  };

  const accentColor = "var(--accent-color)";
  const statusMessage = (status as any)?.message ?? "Patterns need time. Keep going.";
  const logCount = (status as any)?.count ?? 0;

  if (submitted) {
    return (
      <AtmosphericPage contained atmosphere={{ scrimVariant: "content", opacityScale: 0.4 }} contentClassName="flex flex-col items-center justify-center h-full gap-6 fade-in">
        <div
          className="text-5xl font-serif tracking-tight"
          style={{ color: accentColor, fontFamily: "var(--font-display)" }}
          data-testid="text-logged-confirmation"
        >
          Logged.
        </div>
        <div className="flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          <span className="font-mono">
            {mood.toUpperCase()}
            {secondaryMood ? ` + ${secondaryMood.toUpperCase()}` : ""}
          </span>
          {energyLevel ? <span style={{ fontFamily: "var(--font-display)" }}>ENERGY {energyLevel}/10</span> : null}
          <span className="font-mono">{new Date().toLocaleDateString("en-US", { weekday: "long" }).toUpperCase()}</span>
        </div>
      </AtmosphericPage>
    );
  }

  return (
    <AtmosphericPage contained atmosphere={{ scrimVariant: "content", opacityScale: 0.4 }} contentClassName="flex flex-col h-full overflow-y-auto gap-6 px-6 py-6 max-w-xl mx-auto w-full">
      <div>

        <div className="w-full pb-4 flex items-baseline justify-between gap-2" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
          <div>
            <div className="font-mono" style={{ color: "var(--text-muted)" }}>
              {new Date().toLocaleDateString("en-US", {
                weekday: "long", month: "long", day: "numeric"
              }).toUpperCase()}
            </div>
            <div className="font-semibold" style={{ color: accentColor, transition: "color 0.4s ease", fontFamily: "var(--font-display)" }}>
              {logCount} {logCount === 1 ? "entry" : "entries"}
            </div>
          </div>
          <div className="mt-2 text-sm" style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)" }}>
            {statusMessage}
          </div>
        </div>

        <div className="flex flex-col" style={{ gap: "20px", marginTop: "20px" }}>
        <MirrorCard label="WHEN">
          <TimeOfDaySelector value={timeOfDay} onChange={setTimeOfDay} accentColor={accentColor} />
        </MirrorCard>

        <MirrorCard label="HOW IT FEELS">
          <div className="w-full flex flex-col items-center" style={{ marginTop: "-4px" }}>
            <MoodRing selected={mood} onChange={handleMoodChange} size={280} />
          </div>
        </MirrorCard>

        <MirrorCard label="INTENSITY">
          <IntensitySlider value={moodIntensity} onChange={setMoodIntensity} accentColor={accentColor} />
        </MirrorCard>

        <MirrorCard label="CONTEXT">
          <ContextTagPicker selected={selectedTags} onToggle={toggleTag} accentColor={accentColor} />
        </MirrorCard>

        <MirrorCard label="MOMENT">
          <div
            className="relative"
            style={{
              border: "1px solid var(--border-medium)",
              borderRadius: "12px",
              background: "var(--surface-1)",
            }}
          >
            <textarea
              value={moment}
              onChange={e => {
                if (e.target.value.length <= MAX_CHARS) setMoment(e.target.value);
              }}
              placeholder="What's worth remembering about right now?"
              rows={3}
              data-testid="input-moment"
              className="w-full bg-transparent text-sm resize-none outline-none p-4 placeholder:italic placeholder:opacity-60"
              style={{
                color: "var(--fg-raw)",
                fontFamily: "var(--font-reflective)",
                fontSize: "0.9375rem",
                lineHeight: "1.6",
              }}
            />
            <div className="absolute bottom-2 right-3" style={{ color: "var(--text-muted)", fontFamily: "var(--font-display)" }}>
              {moment.length}/{MAX_CHARS}
            </div>
          </div>
        </MirrorCard>

        <div className="w-full">
          <button
            onClick={() => setShowOptional(!showOptional)}
            data-testid="button-toggle-optional"
            className="touch-target flex items-center gap-2 w-full py-2 transition-all"
            style={{ color: "var(--text-muted)", fontFamily: "var(--font-display)" }}
          >
            <div className="flex-1 h-px flex items-center gap-1.5 px-2" style={{ background: "var(--surface-3)" }} />
            <span>
              {showOptional ? "less detail" : "more detail"}
              {showOptional ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </span>
            <div className="flex-1 h-px" style={{ background: "var(--surface-3)" }} />
          </button>
        </div>

        {showOptional && (
          <div className="space-y-6 fade-in w-full">
            <div>
              <div className="text-xs font-mono tracking-wider mb-2 font-normal ml-2">
                UNDERTONE
                <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                  what else is in there
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="secondary-moods">
                {SECONDARY_MOODS.map(sm => {
                  const isSelected = secondaryMood === sm;
                  return (
                    <button
                      key={sm}
                      onClick={() => setSecondaryMood(isSelected ? null : sm)}
                      data-testid={`secondary-${sm}`}
                      className="touch-target px-2.5 py-1 text-xs tracking-wider transition-all duration-200"
                      style={{
                        color: isSelected ? "var(--fg-raw)" : "var(--text-muted)",
                        border: `1px solid ${isSelected ? "var(--border-strong)" : "var(--border-light)"}`,
                        background: isSelected ? "var(--surface-3)" : "transparent",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        borderRadius: "6px",
                        fontFamily: "var(--font-display)",
                        textTransform: "uppercase",
                      }}
                    >
                      {sm}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="w-full text-xs font-mono tracking-wider mb-3 font-semibold">
              <div>
                ENERGY
                <span className="ml-2 font-normal" style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                  what the tank looks like
                </span>
              </div>
              <EnergyMeter value={energyLevel} onChange={setEnergyLevel} accentColor={accentColor} />
            </div>

            <div className="w-full text-xs font-mono tracking-wider mb-3 font-semibold">
              <div>
                LAST NIGHT
                <span className="ml-2 font-normal" style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                  how you slept
                </span>
              </div>
              <SleepMeter value={sleepQuality} onChange={setSleepQuality} />
            </div>
          </div>
        )}

        <div className="w-full pt-2 py-3 text-sm tracking-wider transition-all duration-200">
          <button
            onClick={handleSubmit}
            disabled={logMutation.isPending}
            data-testid="button-submit-log"
            className="touch-target w-full disabled:opacity-60"
            style={{
              color: accentColor,
              border: `1px solid color-mix(in srgb, ${accentColor} 25%, transparent)`,
              background: `color-mix(in srgb, ${accentColor} 5%, transparent)`,
              borderRadius: "10px",
              fontFamily: "var(--font-display)",
              fontWeight: 500,
            }}
          >
            {logMutation.isPending ? "Logging..." : "Log"}
          </button>
          {logMutation.isError && (
            <div className="mt-2 text-xs text-destructive text-center">
              Failed to log. Try again.
            </div>
          )}
        </div>

        <div
          className="text-xs text-center leading-relaxed pb-6"
          style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontStyle: "italic", fontSize: "0.9375rem", fontWeight: 400 }}
        >
          What you log here is yours. Always.
        </div>
        </div>
      </div>
    </AtmosphericPage>
  );
}
